/**
 * Local Thermal Print Agent Service
 * Bridges React LIMS frontend to native Windows thermal printer queues, local serial/USB, and raw sockets.
 * Also provides clear diagnostics for:
 * - AGENT_OFFLINE: Local helper service is not running
 * - PRINTER_OFFLINE: Printer powered off or disconnected
 * - DRIVER_MISSING: Windows driver not found or unsupported
 * - READY: Successfully dispatched directly to thermal printer
 */

const AGENT_URLS = ['http://127.0.0.1:19826', 'http://localhost:19826'];
let activeAgentUrl = AGENT_URLS[0];
const AGENT_TIMEOUT_MS = 2500;

export const thermalAgentService = {
  /**
   * Checks whether the local print agent is active and running.
   */
  async checkAgentHealth() {
    for (const url of AGENT_URLS) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), AGENT_TIMEOUT_MS);
        const res = await fetch(`${url}/status`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        if (res.ok) {
          activeAgentUrl = url;
          const data = await res.json();
          return {
            isOnline: true,
            status: 'READY',
            version: data.version || '1.0.0',
            printers: data.printers || []
          };
        }
      } catch (err) {
        // try next URL
      }
    }
    return {
      isOnline: false,
      status: 'AGENT_OFFLINE',
      error: 'Local Print Agent is not reachable on port 19826'
    };
  },

  /**
   * Queries list of Windows thermal printers detected by the local agent.
   */
  async getInstalledPrinters() {
    try {
      const health = await this.checkAgentHealth();
      if (!health.isOnline) return [];
      const res = await fetch(`${activeAgentUrl}/printers`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        const json = await res.json();
        return json.printers || [];
      }
      return [];
    } catch {
      return [];
    }
  },

  /**
   * Sends raw thermal payload (ZPL / TSPL / EPL / ESC-POS) directly to the designated thermal printer.
   */
  async sendRawThermalJob({ printerName, protocol = 'ZPL', rawPayload, copies = 1, jobTitle = 'LIS Specimen Barcode' }) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);

      const res = await fetch(`${activeAgentUrl}/print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          printerName,
          protocol,
          rawPayload,
          copies,
          jobTitle
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        const result = await res.json();
        return {
          success: true,
          status: 'PRINTED',
          jobId: result.jobId || 'LOCAL-1',
          message: result.message || 'Thermal label dispatched to printer'
        };
      }

      const errJson = await res.json().catch(() => ({}));
      return {
        success: false,
        status: errJson.status || 'PRINT_FAILED',
        error: errJson.error || `Agent returned status ${res.status}`
      };
    } catch (err) {
      return {
        success: false,
        status: 'AGENT_OFFLINE',
        error: 'Local Print Agent is offline. Vector Thermal simulation will be used.'
      };
    }
  },

  /**
   * Triggers a hardware test print directly through agent or fallback.
   */
  async testPrint(printer) {
    const testZpl = `^XA^PW400^LL200^FO50,30^A0N,28,24^FDTEST PRINT SUCCESS^FS^FO50,70^A0N,20,18^FD${printer?.printerName || 'LIMS THERMAL'}^FS^FO50,110^BY2,2.5,50^BCN,50,Y,N,N^FDTEST123456^FS^XZ`;
    return this.sendRawThermalJob({
      printerName: printer?.windowsPrinterName || printer?.printerName,
      protocol: printer?.protocol || 'ZPL',
      rawPayload: testZpl,
      copies: 1,
      jobTitle: 'Diagnostic Thermal Test'
    });
  },

  /**
   * Retrieves virtual spool job history from Local Agent.
   */
  async getRecentJobs() {
    try {
      const res = await fetch(`${activeAgentUrl}/jobs`);
      if (res.ok) {
        const json = await res.json();
        return json.jobs || [];
      }
      return [];
    } catch {
      return [];
    }
  },

  /**
   * Clears virtual spool directory.
   */
  async clearSpool() {
    try {
      const res = await fetch(`${activeAgentUrl}/clear-spool`, { method: 'POST' });
      return res.ok;
    } catch {
      return false;
    }
  }
};

export default thermalAgentService;
